
import { Component, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import { shiftDateWithTimezone } from '@/helpers/date-helper';
import { contextSportIs, getSportPageContext, SingleStepOverviewContext } from '@/helpers/sports/config';
import { SnowplowSportContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { Platform, SportEventAggregation, SportEventStatus, SportType, SraPlacement } from '@/@types/graphql-types';
import { CollectionType } from '@/enums/collection-type';

import { SportCompetitionV2 } from '@/@types/graphql-types';
import { getOverviewSportSpinningText, SportOverviewSpinningTextOptions } from '@/components/sports/spinning-texts';
import { isMultiStepSport, SportLocale } from '@/enums/sports';
import type { WebLocale } from '@/enums/web-locale';
import { LocaleResponse } from '@/helpers/locale-helper';
import { BreadcrumbItem, SportPageType } from '@/interfaces/sport-graphql';
import { FilterCollection } from '@/stores/filter.store';

import {
	GetSingleStepSportOverviewDocument,
	GetSingleStepSportOverviewQueryVariables,
} from '@/pages/graphql/queries/GetSingleStepSportOverview.query';

import ProvideUpcomingSportEvents from '@/components/sports/single-step/ProvideUpcomingSportEvents.vue';

import AdUnit from '@/components/AdUnit.vue';
import Breadcrumbs from '@/components/sports/common/Breadcrumbs.vue';
import SingleStepEventEncounterList from '@/components/sports/common/SingleStepEventEncounterList.vue';
import Header from '@/components/sports/common/Header.vue';
import CompetitionEventsCalendar from '@/components/sports/common/overview/CompetitionEventsCalendar.vue';
import ProviderStreamsComparator from '@/components/sports/common/provider-streams-comparator/ProviderStreamsComparator.vue';
import SportsLayout from '@/components/sports/common/SportsLayout.vue';
import SportSpinningText from '@/components/sports/single-step/SportSpinningText.vue';
const StreamLanguageSwitcher = () => import('@/components/sports/common/StreamLanguageSwitcher.vue');

const Language = namespace('language');
const Filter = namespace('filter');
const Meta = namespace('meta');
const Routing = namespace('routing');
const User = namespace('user');

@Component({
	name: 'SingleStepOverviewPage',
	components: {
		ProvideUpcomingSportEvents,
		Breadcrumbs,
		CompetitionEventsCalendar,
		SingleStepEventEncounterList,
		Header,
		ProviderStreamsComparator,
		SportsLayout,
		StreamLanguageSwitcher,
		SportSpinningText,
		AdUnit,
	},
	apollo: {
		overviewData: {
			fetchPolicy: 'cache-and-network',
			query: GetSingleStepSportOverviewDocument,
			variables(): GetSingleStepSportOverviewQueryVariables {
				return this.queryVariables;
			},
			update(data) {
				return data;
			},
			result(_result, _key) {
				this.SET_JSON_LD(this.schemaData);
			},
			skip() {
				if (this.$route.name != null && this.$route.name.includes('overview')) return false;
				return true;
			},
		},
	},
})
export default class SingleStepOverviewPage extends Vue {
	@Language.Getter country: string;
	@Language.Getter language: string;
	@Language.Getter localeObject: LocaleResponse;
	@Language.State webLocale: WebLocale;

	@Filter.Getter(CollectionType.SPORTS) filtersSport: FilterCollection;

	@Meta.Mutation SET_JSON_LD: (content: string) => void;

	@Routing.State urlMetadata: any;

	@User.Getter isLoggedIn: () => boolean;

	// SPORT CONTEXT
	context = {} as SingleStepOverviewContext;

	jsDisabled = true;

	@Watch('$route.meta.sport', { immediate: true })
	setContext(newMeta: SportType | undefined) {
		if (newMeta && newMeta !== this.context.sportType && !isMultiStepSport(newMeta)) {
			this.context = getSportPageContext({
				sport: this.$route.meta!.sport as SportType,
				pageType: SportPageType.SportOverview,
			});
		}
	}

	// QUERY & DATA
	overviewData: Partial<SportCompetitionV2> = {};
	displayDate = shiftDateWithTimezone(this.$store.getters['language/localeObject'].timezone);

	get SraPlacement() {
		return SraPlacement;
	}

	get competitions() {
		return this.overviewData?.sportEventBucketsV2?.edges ?? [];
	}

	get queryVariables(): GetSingleStepSportOverviewQueryVariables {
		return {
			bucketSize: 6,
			country: this.country,
			filter: {
				startAt: {
					from: this.displayDate.toISOString(),
					to: shiftDateWithTimezone(this.localeObject.timezone, 1, this.displayDate).toISOString(),
				},
				packages: this.filtersSport.providers ?? [],
				sports: [this.context.sportType],
			},
			first: this.jsDisabled ? 6 : 3,
			groupBy: SportEventAggregation.Competition,
			language: this.language,
			platform: Platform.Web,
			timezoneOffset: 0,
		};
	}

	get isDataLoading() {
		return this.$apollo.queries.overviewData.loading;
	}

	get pageInfo() {
		return this.overviewData?.sportEventBucketsV2?.pageInfo ?? null;
	}

	get hasLoadMore() {
		return this.pageInfo?.hasNextPage && this.competitions?.length !== 0;
	}

	handleLoadMore() {
		if (!this.pageInfo?.hasNextPage) return;

		this.$apollo.queries.overviewData.fetchMore({
			variables: { after: this.pageInfo?.endCursor },
			updateQuery: (previousResult, { fetchMoreResult }) => ({
				sportEventBucketsV2: {
					__typename: previousResult.sportEventBucketsV2.__typename,
					edges: [...previousResult.sportEventBucketsV2.edges, ...fetchMoreResult.sportEventBucketsV2.edges],
					pageInfo: fetchMoreResult.sportEventBucketsV2.pageInfo,
				},
			}),
		});
	}

	get breadcrumbs(): BreadcrumbItem[] {
		return [
			// e.g. Sport
			{ name: this.$tc('WEBAPP_SPORTS'), routeName: 'app.sports.all' },

			// e.g. Fussball
			{
				name: this.$tc(this.context.sportName),
				objectType: this.context.pageType,
				url: this.context.getUrl(this.webLocale as SportLocale),
			},
		];
	}

	getSpinningText(options: SportOverviewSpinningTextOptions) {
		return getOverviewSportSpinningText(options);
	}

	get schemaData() {
		const schemaDataObject = {
			'@context': 'http://schema.org',
			'@graph': this.schemaCompetitions,
		};
		return JSON.parse(JSON.stringify(schemaDataObject).replace(/&quot;/g, '"')) || '';
	}

	get schemaCompetitions() {
		return this.overviewData?.sportEventBucketsV2?.edges?.map(competition => {
			const events = competition?.node?.edges?.map(event => {
				const eventData = event.node;

				// @ts-ignore
				const { key, params } = this.context.getEventTitle(eventData, this.webLocale);
				const eventTitle = this.$t(key, params);

				const prices =
					eventData?.offers?.map((offer: any) => offer.retailPrice as number).filter((v: any) => v != null) ??
					[];

				let eventStatus = '';
				switch (eventData.content?.status) {
					case SportEventStatus.Postponed:
						eventStatus = 'EventPostponed';
						break;
					case SportEventStatus.Cancelled:
						eventStatus = 'EventCancelled';
						break;
					default:
						eventStatus = 'EventScheduled';
				}

				return {
					'@context': 'http://schema.org',
					'@type': 'BroadcastEvent',
					name:
						`${eventTitle} ${competition?.node?.edges?.[0]?.node?.competition?.content?.name} ${
							eventData.competition?.activeSeason?.name ?? ''
						}` ?? '',
					isLiveBroadcast: (eventData?.offers ?? []).length > 0,
					startDate: eventData.content?.startAt ?? '',
					endDate: eventData.content?.endAt ?? '',
					broadcastOfEvent: {
						'@type': 'SportsEvent',
						sport: this.context.sportType.toLowerCase().replace(/_/g, ' '),
						eventStatus: `https://schema.org/${eventStatus}`,
						name:
							`${eventTitle} ${competition?.node?.edges?.[0]?.node?.competition?.content?.name} ${
								eventData.competition?.activeSeason?.name ?? ''
							}` ?? '',
						awayTeam: {
							'@type': 'SportsTeam',
							name:
								// @ts-ignore
								eventData?.competitors?.awayTeam?.competitors
									.map((c: any) => c.content.name)
									.find(Boolean) ?? '',
							sport: this.context.sportType.toLowerCase().replace(/_/g, ' '),
						},
						homeTeam: {
							'@type': 'SportsTeam',
							name:
								// @ts-ignore
								eventData?.competitors?.homeTeam?.competitors
									.map((c: any) => c.content.name)
									.find(Boolean) ?? '',
							sport: this.context.sportType.toLowerCase().replace(/_/g, ' '),
						},
						startDate: eventData.content?.startAt ?? '',
						endDate: eventData.content?.endAt ?? '',
						location: {
							'@type': 'Place',
							name: eventData.content?.venue?.name,
						},
						offers: eventData?.offers
							? {
									'@type': 'AggregateOffer',
									highPrice: Math.max(...prices) ?? undefined,
									lowPrice: Math.min(...prices) ?? undefined,
									priceCurrency: eventData?.offers.map((offer: any) => offer.currency).find(Boolean),
									offerCount: eventData?.offers.length,
									offers: eventData?.offers.map((offer: any) => ({
										'@type': 'Offer',
										name: `${eventTitle} ${offer.presentationType} ${offer.monetizationType}`,
										price: offer.retailPrice,
										priceCurrency: offer.currency,
										url: offer.standardWebURL,
									})),
							  }
							: null,
						audience: {
							'@type': 'Audience',
							name: 'Sports Fans',
						},
					},
				};
			});

			return {
				'@type': 'EventSeries',
				name: competition?.node?.edges?.[0]?.node?.competition?.content?.name,
				subEvents: events,
			};
		});
	}

	get translatedCountry() {
		if (this.country === 'US') return this.$t('WEBAPP_COUNTRY_THE_US');
		if (this.country === 'GB') return this.$t('WEBAPP_COUNTRY_THE_GB');

		return this.$t('WEBAPP_COUNTRY_' + this.country);
	}

	get availableStreamLanguages() {
		const hasAudioStreams = contextSportIs.esports(this.context) && this.context.meta.showStreamSwitcher;

		if (!hasAudioStreams) return [];

		const languages = this.competitions
			.flatMap(competition => competition.node.edges ?? [])
			.flatMap(event => event.node.offers ?? [])
			.map(offer => offer.audioLanguage);

		return [...new Set(languages)];
	}

	// TRACKING
	get trackingContext() {
		return new SnowplowSportContext(this.$route.meta!.sport as SportType, SportPageType.SportOverview);
	}

	activated() {
		// note: all sport pages are excluded from automatic page tracking
		/**
		 * setTimeout added here to prevent pageView to be triggered before link click track event.
		 * that's because when adding the @click.native to router-link the track event will get triggered
		 * after the navigation which causes the pageView to get triggered first.
		 */
		setTimeout(() => {
			TrackingHelper.trackPage(this.$route.path, [this.trackingContext]);
		});
	}
}
